import { YouTubePlayer } from 'youtube-player/dist/types';
import pubSub from '../utility/pub-sub';
import getScript from '../utility/get-script';
import {
  VIDEO_PLAY,
  PLATFORM_VIDEO_PLAY,
  TOGGLE_PROMOBOX_EVENT,
} from '../utility/constants';
import { STOP_CAROUSEL_VIDEO_PLAYER } from '../carousel/carousel.constants';

import {
  YT_VIDEO_CLASSNAME,
  YT_VIDEO_IFRAME_API,
  YT_VIDEO_DATA_ID,
  YT_VIDEO_CONTEXT_ELEMENT,
  YT_VIDEO_PAUSE_BTN,
  YT_VIDEO_PLAY_BTN,
  YT_VIDEO_OPTIONS,
  YouTubeOnStateChangeEvent,
} from './youtube-video.constants';
import {
  getPercentage, intervalTracker, onPlayerReady, onPlayerStateChange, setVideoId,
} from './youtube-video-utils';

class YoutubeVideo {
  videos;

  players: YouTubePlayer[];

  constructor() {
    this.videos = document.querySelectorAll('.js-youtube-video');
    this.players = [];
  }

  init() {
    if (this.videos.length) {
      getScript(YT_VIDEO_IFRAME_API, () => {
        // Create ID if not exist
        this.videos.forEach((video, index) => {
          if (!video.id) {
            video.setAttribute('id', `${YT_VIDEO_CLASSNAME}-${index}`);
          }
        });

        // When YT loaded create instance for each YT Player
        if (window.YT) {
          window.YT.ready(
            () => this.videos.forEach((video) => this.createYTInstance(video)),
          );
        }

        // Subscribe to all Platform videos (JWPlayer) if some of them start to play
        pubSub.subscribe(PLATFORM_VIDEO_PLAY, this.stopAll.bind(this));
        pubSub.subscribe(STOP_CAROUSEL_VIDEO_PLAYER, this.stopAll.bind(this));

        // Stop video inside Promobox Accordion Modal component when modal/accordion close
        pubSub.subscribe(TOGGLE_PROMOBOX_EVENT, (data) => {
          if (!data.isPromoboxModalOpen) {
            const promobox = document.querySelector(data.element);
            const promoboxVideo = promobox.querySelector(`.${YT_VIDEO_CLASSNAME}`);
            const videoId = promoboxVideo.id;
            this.players.forEach(async (playerVideo) => {
              const playerVideoElement = await playerVideo.getIframe();
              const iframeId = playerVideoElement.id;
              if (
                iframeId
                && iframeId === videoId
                && Object.prototype.hasOwnProperty.call(playerVideo, 'stopVideo')
              ) {
                return playerVideo.stopVideo();
              }
              return null;
            });
          }
        });
      });
    }
  }

  /**
   * Create YT API instance
   * @param video - video holder element
   * @returns YT player instance
   */
  createYTInstance(video: Element) {
    const isIdExist = video.hasAttribute('id');

    if (!isIdExist) {
      video.setAttribute('id', `${YT_VIDEO_CLASSNAME}-${setVideoId()}`);
    }
    let player: YouTubePlayer = null;
    let playBtn = null;
    let pauseBtn = null;
    let context = null;

    const elementId = video.id;
    const youtubeId = video.getAttribute(YT_VIDEO_DATA_ID);
    // Get context of video since we cta that opens modal with another cta
    context = video.closest(YT_VIDEO_CONTEXT_ELEMENT);

    if (context) {
      // TODO
      if (context.querySelectorAll(YT_VIDEO_PAUSE_BTN)) {
        pauseBtn = context.querySelectorAll(YT_VIDEO_PAUSE_BTN);
      }

      if (context.querySelector(YT_VIDEO_PLAY_BTN)) {
        playBtn = context.querySelector(YT_VIDEO_PLAY_BTN);
      }
    }
    // check if other YT video has autoplay options so we can remove it
    const isAutoplayExists = Array.from(this.players).some(async (ytPlayer: YouTubePlayer) => {
      const iframe = await ytPlayer.getIframe();
      return iframe.dataset.autoplay === '1';
    });

    // Get player options
    const controls = parseInt(video.getAttribute(YT_VIDEO_OPTIONS.CONTROLS), 10);
    const autoplay = isAutoplayExists ? 0 : parseInt(video.getAttribute(YT_VIDEO_OPTIONS.AUTOPLAY), 10);
    const mute = autoplay ? 1 : parseInt(video.getAttribute(YT_VIDEO_OPTIONS.MUTE), 10);
    const loop = parseInt(video.getAttribute(YT_VIDEO_OPTIONS.LOOP), 10);

    const element = document.getElementById(elementId);
    let intervalID: ReturnType<typeof setTimeout>;
    let videoPlays = false;
    const currentProgress: number[] = [];

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    player = new window.YT.Player(element, {
      videoId: youtubeId,
      playerVars: {
        controls,
        rel: 0,
      },
      events: {
        onReady: (e: YouTubeOnStateChangeEvent) => {
          onPlayerReady(e.target, autoplay, loop, mute);
        },
        onStateChange: (e: YouTubeOnStateChangeEvent) => {
          window.clearInterval(intervalID);

          if (e.data === window.YT.PlayerState.PLAYING) {
            window.clearInterval(intervalID);
            intervalID = setInterval(async () => {
              const currentPercentage = await getPercentage(e.target);
              const progress = [25, 50, 75];
              if (
                currentPercentage > 0 && progress.some((target) => target === currentPercentage)
              ) {
                const isInProgresAlready = currentProgress.includes(currentPercentage);

                if (!isInProgresAlready) {
                  intervalTracker(e.target);
                  currentProgress.push(currentPercentage);
                }
              }
            }, 100);
          }
          onPlayerStateChange(
            e.target,
            e.data,
            this.players,
            () => videoPlays,
            (isPlaying: boolean) => {
              videoPlays = isPlaying;
            },
          );
        },
      },
    });

    this.players.push(player);

    if (playBtn) {
      playBtn.addEventListener('click', () => {
        player.playVideo();
        pubSub.publish(VIDEO_PLAY, {});
      });
    }

    if (pauseBtn) {
      Array.from(pauseBtn, (button: HTMLElement) => button.addEventListener('click', () => {
        player.stopVideo();
      }));
    }
  }

  /**
   * Stop all youtube videos
   */
  stopAll() {
    this.players.forEach((video) => (typeof video.stopVideo === 'undefined' ? null : video.stopVideo()));
  }

  /**
   * Return all initialized YouTube videos expose to other components
   * @returns {YT} - list of YouTube iframe api players
   */
  getAllPlayers() {
    return this.players;
  }
}

export default YoutubeVideo;
